<template>
  <div class="autocomplete-wrapper" :class="additionalClass">
    <div id="autocomplete-default" class="search-input" v-if="useDefault"></div>
    <div class="search-input" v-else>
      <template v-if="rightIcon && !isCompleted">
        <a
          :class="{completed: isCompleted}"
          class="uk-form-icon uk-form-icon-flip"
          v-on:click="onSearchClicked"
          uk-icon="icon: search"
        />
      </template>
      <template v-else>
        <span
          :class="{completed: isCompleted}"
          class="uk-form-icon prefix-search-icon material-symbols-sharp"
          :data-icon="isCompleted ? 'check' : 'distance'"
        />
      </template>
      <input
        autocomplete="off"
        style="display:block"
        @click.stop="onFocused"
        id="autocomplete"
        class="custom uk-input uk-form-small"
        :class="{'active': isCompleted}"
        :placeholder="placeholder"
        type="text"
        @blur="onBlur"
        @input="onInput"
        @focus="onFocused"
        @keydown.enter="onEnter"
        enterkeyhint="search" />
    </div>

    <div class="suggestions-wrapper" v-show="suggestions.items.length > 0" :class="{
      hide: suggestions.items.length === 0
    }">
      <i class="material-symbols-sharp">arrow_drop_up</i>
      <div class="suggestions">
        <div class="suggestion" :class="{ focused: suggestions.focusedIndex === index }" v-for="(suggestion, index) in suggestions.items" v-wave @click="onSuggestClicked(suggestion)">
          <span class="name" v-html="suggestion.name"></span>
          <span class="address" v-html="suggestion.address"></span>
          <i class="material-symbols-sharp">arrow_forward_ios</i>
        </div>
      </div>
      <div class="suggestions-footer" v-if="showPoweredBy">
        <img src="https://storage.googleapis.com/storelocator-v3-static/assets/common/GoogleMaps_Logo_Gray_2x.png">
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * 地名検索（New Places）
 *
 * Places / Geocoding の２APIを使用
 * 地図利用料に留意して使用すること
 *
 * powered by のロゴは以下をGCSに設置
 * https://developers.google.com/maps/documentation/places/web-service/policies#logo
 *
 * @file AutocompleteV2.vue
 * @description Google Maps Places API を用いた位置検索のためのコンポーネント
 */
import { ref, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { geocoding } from '@/utils/geocoding'

import MapService from '@/services/MapService.ts'

import { _debounce } from '@/utils/common'

const store = useStore()

const emit = defineEmits([
  // 位置の取得後
  'onSearchPlace',
])

const props = defineProps({
  /**
   * True：標準のウィジェットに切り替え（現状では使わない）
   */
  useDefault: {
    type: Boolean,
    default: false
  },
  /**
   * プレースホルダー
   * 多言語化するときは注意
   */
  placeholder: {
    type: String,
    default: storelocator.messages.map_search_place
  },
  /**
   * デフォルトで入力するクエリ
   */
  query: {
    type: String,
    default: null,
  },
  /**
   * 追加クラス
   * 画面表示の調整に用いる想定
   * 'points-page'を指定すると、一覧ページのデザインに合わせて表示調整される
   * それ以外は案件ごとに個別のCSSで調整する想定
   */
  additionalClass: {
    type: String,
    default: "",
  },
  /**
   * 地名検索結果が有効かどうか
   * ポイント一覧ページで利用する想定. 地名検索結果を用いたソートが有効な場合、true を指定する
   * この値の影響は見た目だけで、動作には影響しない
   */
  isCompleted: {
    type: Boolean,
    default: false,
  },
  /**
   * 右側に検索アイコンを表示するかどうか
   */
  rightIcon: {
    type: Boolean,
    default: false,
  },
  /**
   * true: Places Autocompleteを使う
   * コスト調整が必要な場合用
   */
  useAutocomplete: {
    type: Boolean,
    default: true,
  },
  /**
   * true: Googleのpowered byを非表示（地図上にロゴが出ていれば必要ない）
   */
  showPoweredBy: {
    type: Boolean,
    default: true,
  },
  /**
   * autocompleteで使用するリージョンコード
   */
  regionCode: {
    type: String,
    default: 'JP',
  },

  /**
   * AutoCompleteのサジェストに表示する国のリージョンコード
   * 空の場合はすべての国を対象とする
   */
  includedRegionCodes: {
    type: Array,
    default: ['JP'],
  },
})

/**
 * true: Autocompleteにフォーカスがある
 * キー操作の制御用
 */
let isFocused = false

onMounted(async () => {
  // console.debug('onMounted', props.useDefault)

  // 公式ウィジェット
  if (props.useDefault) {
    const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement({
      requestedRegion: props.regionCode,
    })

    const autocompleteWrapper = document.getElementById('autocomplete-default')
    placeAutocomplete.id = 'autocomplete'
    placeAutocomplete.classList.add('default')
    autocompleteWrapper.appendChild(placeAutocomplete)

    placeAutocomplete.addEventListener('gmp-select', async ({ placePrediction }) => {
      onSuggestClicked({ placePrediction })
    })

    placeAutocomplete.addEventListener('keydown', (event) => {
      // 何もしない
      // 標準のウィジェットだと、入力値を拾えない（入力ごとにイベントから
    })
  // 独自ウィジェット
  } else {
    if (props.query) {
      document.getElementById("autocomplete").value = props.query
    }

    // 候補表示中のキー移動
    document.addEventListener('keydown', (event) => {
      if (isFocused) {
        switch (event.key) {
          case 'ArrowUp':
            if (suggestions.value.focusedIndex > 0) {
              suggestions.value.focusedIndex--
            }

            break;

          case 'ArrowDown':
            if (suggestions.value.focusedIndex === null) {
              suggestions.value.focusedIndex = 0
            } else if (suggestions.value.focusedIndex < suggestions.value.items.length - 1) {
              suggestions.value.focusedIndex++
            }

            break;

          default:
            break;
        }
      }
    })
  }
})

/**
 * Enter押下時
 */
function onEnter(e) {
  // console.debug('onEnter', isFocused, suggestions.value.focusedIndex)

  // 候補の選択中：フォーカス中の候補に移動
  if (isFocused && suggestions.value.focusedIndex >= 0) {
    const suggestion = suggestions.value.items[suggestions.value.focusedIndex]
    if (suggestion) {
      onSuggestClicked(suggestion)

      return
    }
  }

  // 入力値に移動（変換確定の時点では発火しない）
  if (!e.isComposing) {
    document.getElementById("autocomplete").blur()
    onSearchClicked()
    resetSuggestions()
  }
}

/**
 * 候補取得のリクエストを間引く間隔
 * SPはより長くする（入力がPCより遅いと考えられるため）
 * 
 * 汎用性を考慮して一旦決め打ち（SLに依存させない）、propsで指定させることも検討の余地がある
 */
const debounceDelay = window.innerWidth <= 768 ? 400 : 200

// console.debug('debounceDelay', debounceDelay)

// コンポーネント内で一度だけデバウンス関数を生成
const debouncedUpdateSuggestions = _debounce(function() {
  updateSuggestions()
}, debounceDelay)

/**
 * Autocompleteに書き換えられていないクエリをもっておきたい
 */
let inputtedPlaceQuery = ''

/**
 * 変更時
 */
async function onInput() {
  inputtedPlaceQuery = document.getElementById('autocomplete').value

  // 入力が空の場合は早期リターン
  if (!inputtedPlaceQuery) {
    resetSuggestions()
    return
  }

  // 最初はやや遅れを感じるが、即実行すると中途半端な段階で送信しがちになるので、許容
  debouncedUpdateSuggestions()
}

/**
 * イベントが複数発火したときの防止用
 */
let searching = false

/**
 * Places Autocompleteのセッショントークン
 * 
 * Places Details取得の時点で破棄する
 */
let sessionToken = props.useDefault ? null : new google.maps.places.AutocompleteSessionToken()

/**
 * New Places の位置の候補
 */
const suggestions = ref({
  /**
   * フォーカスされている候補のインデックス
   * null: 未選択
   */
  focusedIndex: null,
  /**
   * 場所の候補
   * 
   * Places Autocompleteの応答をもとにしたもの
   * @type {Array<{name: string, originalName: string, address: string, placeId: string, types: Array<string>, placePrediction: google.maps.places.PlacePrediction}>}
   */
  items: []
})

// console.debug('set up places autocomplete session', sessionToken)

/**
 * New Places
 * 位置の候補をリセット
 */
function resetSuggestions() {
  // console.debug('resetSuggestions')

  if (props.useDefault) {
    return
  }

  // 多方面から呼ばれるので、リセット済ならスキップ
  if (suggestions.value.items.length === 0) {
    // console.debug('skip reset suggestions')
    return
  }

  // console.debug('suggestions', suggestions.value.items)

  // 場所の候補をリセット
  suggestions.value.items = []
  suggestions.value.focusedIndex = null
  latestQuery = ''

  // セッショントークンをリセット（セッションを使い回しすぎると、課金のカウントが想定外になる場合があるので防止）
  sessionToken = new google.maps.places.AutocompleteSessionToken()
}

/**
 * 最新の入力値（連続で同じクエリを送信するの防止用）
 */
let latestQuery = ''

/**
 * New Places
 * 位置の候補を更新
 * 
 * TODO　セッショントークンが失効する可能性が理論上はあり得る、失効が見られるのであれば要対策
 */
async function updateSuggestions() {
  if (!props.useAutocomplete) {
    // console.debug('Places Autocomplete disabled')
    return
  }

  if (props.useDefault) {
    return
  }

  // 空
  if (!inputtedPlaceQuery) {
    resetSuggestions()
    return
  }

  // 短すぎる
  if (inputtedPlaceQuery.length < 2) {
    resetSuggestions()
    return
  }

  // 直前と同じ
  if (inputtedPlaceQuery === latestQuery) {
    // console.debug('duplicate query, skip update suggestions')
    return
  }

  // 場所の候補を取得
  // https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data?hl=ja#AutocompleteRequest
  const autocompleteParams = {
    input: inputtedPlaceQuery,
    region: props.regionCode,
    // 距離がほしければ指定
    // origin: MapService.map?.getCenter(),
    locationBias: MapService.map?.getBounds(),
    sessionToken: sessionToken,
  }

  // console.debug('updateSuggestions', autocompleteParams)

  // リージョンを指定
  if (props.includedRegionCodes && props.includedRegionCodes.length > 0) {
    autocompleteParams.includedRegionCodes = props.includedRegionCodes
  }

  latestQuery = inputtedPlaceQuery

  let response
  
  try {
    response = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(autocompleteParams)
  } catch (error) {
    console.warn('Error fetching autocomplete suggestions:', error)
    resetSuggestions()

    // TODO 失敗を記録できた方が望ましい（トークン失効など）

    return
  }

  resetSuggestions()

  response.suggestions.map((s) => {
    // console.debug('suggestion', inputtedPlaceQuery, s.placePrediction.mainText?.text, s)

    const suggestion = {
      name: s.placePrediction.mainText?.text,
      originalName: s.placePrediction.mainText?.text,
      address: s.placePrediction.secondaryText?.text,
      placeId: s.placePrediction.placeId,
      types: s.placePrediction.types,
      placePrediction: s.placePrediction
    }

    // 入力と一致する部分を強調
    if (s.placePrediction.mainText.matches?.length > 0) {
      const matchedName = suggestion.name.substring(s.placePrediction.mainText.matches[0].startOffset, s.placePrediction.mainText.matches[0].endOffset)
      suggestion.name = suggestion.name.replace(
        matchedName,
        '<span class="highlight">' + matchedName + '</span>'
      )
    }

    // 住所は番地以降がマッチしたものとして出てくる？ので同じ強調はなし

    suggestions.value.items.push(suggestion)
  })
}

/**
 * New Places
 * 場所の候補をクリック時
 * @param suggestion suggestions.items[]
 */
async function onSuggestClicked(suggestion) {
  // console.debug('onSuggestClicked', suggestion)

  const placeClient = suggestion.placePrediction.toPlace()

  const placeResponse = await placeClient.fetchFields({
    fields: [
      'location',
      'viewport',
      // これが入るとProにカウントされる、originalNameで代用になることが多いので廃止の方向
      // 'displayName'
    ]
  })

  const place = placeResponse?.place

  if (!place?.location) {
    console.warn('place.location is not found')

    emit('onSearchPlace', {
      query: inputtedPlaceQuery,
      by: 'Places'
    })

    return
  }

  // console.debug('place', suggestion.originalName, place.displayName)

  emit('onSearchPlace', {
    query: inputtedPlaceQuery,
    by: 'Places',
    name: suggestion.originalName,
    location: place.location,
    bounds: place.viewport
  })

  resetSuggestions()

  document.getElementById("autocomplete").value = suggestion.originalName + (suggestion.address ? (' ' + suggestion.address) : '')
}

/**
 * New Places
 * フォーカスが外れたとき
 */
function onBlur() {
  // console.debug('onBlur')

  setTimeout(() => {
    resetSuggestions()
  }, 200) // すぐに候補を消してしまうと、@clickが発火しないので遅らせる

  isFocused = false
}

/**
 * 検索アイコンクリック時
 *
 * この時は候補を取れないので、Geocodingに流す
 */
async function onSearchClicked() {
  // console.debug('onSearchClicked', suggestions.value.items)

  if (searching) {
    // console.debug('intterupt onSearchClicked')
    return
  }

  // ここで候補を取っておかないと、Geocoding中に消えてしまう
  const suggestion = suggestions.value.items?.length > 0
    ? suggestions.value.items[0]
    : null

  searching = true

  // vue の変数だと Autocomplete が書き換えた値を拾えない
  const query = document.getElementById('autocomplete').value

  if (query) {
    let geocoded = null
    try {
      geocoded = await geocoding(query)
    } catch (status) {
      console.warn('geocode failed', status)

      searching = false

      // Geocodingで取れない場合は、Placesの候補に流す
      if (suggestion) {
        // console.debug('onSearchClicked geocoding failed, use places', suggestion)
        onSuggestClicked(suggestion)
        return
      }

      // メッセージ出しはFW依存、ここではしない
      emit('onSearchPlace', {
        query: inputtedPlaceQuery,
        by: 'Geocoding',
        status: status,
        location: null,
        bounds: null
      })

      return
    }

    emit('onSearchPlace', {
      query: inputtedPlaceQuery,
      pref: geocoded.address_components.find((c) => c.types.includes('administrative_area_level_1'))?.long_name,
      city: geocoded.address_components.find((c) => c.types.includes('locality'))?.long_name,
      by: 'Geocoding',
      location: geocoded.geometry.location,
      bounds: geocoded.geometry.viewport
    })

    searching = false
  }

  searching = false
}

function resetQuery() {
  document.getElementById('autocomplete').value = ''
  inputtedPlaceQuery = ''
}

async function onFocused() {
  // 再入力しやすいように消す（微調整ができない欠点になる可能性もある）
  resetQuery()
  resetSuggestions()

  if (store.getters.isPanelClosed) {
    store.dispatch("openPanel")
  }

  updateSuggestions()

  isFocused = true

  // 候補が表示されるようにスクロールさせる
  const autocompleteElement = document.getElementById("autocomplete")
  if (autocompleteElement) {
    autocompleteElement.scrollIntoView({
      behavior: 'smooth'
    })
  }
}

/**
 * 現在地を取得・移動したら入力を消す
 * 表示位置との差が生まれる可能性があり、混乱するため
 */
watch(() => store.getters.currentLocation, () => {
  document.getElementById("autocomplete").value = ""
})

function clear() {
  document.getElementById("autocomplete").value = ""
}

defineExpose({
  clear,
})
</script>

<style lang="scss">
.autocomplete-wrapper {
  width: 100%;
  position: relative;
  border-radius: 4px;
  transition: all .2s;

  .search-input {
    position: relative;

    #autocomplete {
      width: 100%;
      border: none;
      font-size: 16px;
      border-radius: 20px;
      background-color: #eee;
      transition: all .25s;
      height: 40px;
      &.custom {
        padding-left: 16px;
      }
      &::placeholder {
        font-family: system-ui, -apple-system,  sans-serif;
      }
    }
  }

  @keyframes suggestions-fade-in {
    0% {
      opacity: 0;
      max-height: 200px;
    }
    100% {
      opacity: 1;
      max-height: 270px;
    }
  }

  .suggestions-wrapper {
    border-bottom: 1px solid var(--theme-color);
    margin-top: 12px;
    position: absolute;
    width: 100%;
    z-index: 100;

    >i {
      color: var(--theme-color);
      font-size: 60px;
      width: 100%;
      display: block;
      text-align: center;
      margin-bottom: 4px;
      pointer-events: none;
      user-select: none;
      line-height: 0;
    }

    .suggestions {
      animation: suggestions-fade-in .25s ease-out;

      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, .5);
      background: #fff;
      max-height: 270px;
      border: 1px solid var(--theme-color);
      border-bottom: none;
      overflow-y: auto;

      .suggestion {
        font-size: 14px;
        border-bottom: 1px solid #ccc;
        padding: 6px 8px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        cursor: pointer;
        position: relative;

        .highlight {
          font-weight: bold;
        }

        .address {
          font-size: 12px;
          display: block;
          margin-top: 2px;
        }

        i {
          font-size: 16px;
          display: inline-block;
          position: absolute;
          opacity: 1;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          opacity: 0;
        }

        &:hover, &.focused {
          background: #f9f9f9;
          border-left: 3px solid var(--theme-color);
          padding-right: 24px;
          transition: all .1s;

          i {
            opacity: 1;
            right: 6px;
            transition: all .25s;
          }
        }
      }
    }

    .suggestions-footer {
      border: 1px solid var(--theme-color);
      border-top: none;
      background: #fff;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, .5);
      color: #999;
      font-size: 12px;
      text-align: center;
      padding: 6px;
      img {
        width: 98px;
      }
    }
  }

  .uk-form-icon.completed {
    color: #fff;
  }

  &.custom:focus-within {
    margin: 0 -2px 0 -2px;
    width: calc(100% + 4px);
    #autocomplete {
      background: #777;
      border-color: #777 !important;
      color: #fff;
      transition: all .25s;

      &::placeholder {
        color: #aaa;
      }
    }

    .uk-form-icon {
      color: #ccc;
      transition: all .25s;
      &:hover {
        color: #fff;
      }
    }
  }
}
</style>
